import { deepMerge, toNumber } from '@sensenet/client-utils';
import { useRepository } from '@sensenet/hooks-react';
import React, { createContext, useMemo } from 'react';
import { v1 } from 'uuid';
/**
 * Adds a globally unique ID to the shape
 */
const addGuidToShape = shape => {
  shape.guid = v1();
  return shape;
};

/**
 * Concatenates avatar url with host
 * @param {DocumentData} documentData
 */
function changeCreatedByUrlToCurrent(documentData) {
  return comment => {
    return {
      ...comment,
      createdBy: {
        ...comment.createdBy,
        avatarUrl: `${documentData.hostName}${comment.createdBy.avatarUrl}`
      }
    };
  };
}
export const DocumentViewerApiSettingsContext = /*#__PURE__*/createContext({
  canEditDocument: async () => false,
  canHideRedaction: async () => false,
  canHideWatermark: async () => false,
  commentActions: {
    addPreviewComment: async ({
      comment
    }) => ({
      ...comment
    }),
    deletePreviewComment: async () => ({
      modified: false
    }),
    getPreviewComments: async () => []
  },
  getDocumentData: async () => ({
    documentName: 'example',
    documentType: '',
    fileSizekB: 3,
    hostName: '',
    idOrPath: 1,
    pageNum: 1,
    pageCount: 0,
    shapes: {
      annotations: [],
      highlights: [],
      redactions: []
    }
  }),
  getExistingPreviewImages: async () => [],
  isPreviewAvailable: async () => ({
    Height: 0,
    Width: 0,
    Index: 0
  }),
  regeneratePreviews: async () => ({
    PageCount: -1,
    PreviewCount: 0
  }),
  saveChanges: async () => undefined
});
export const createDefaultApiSettings = repo => ({
  regeneratePreviews: async ({
    document,
    abortController
  }) => {
    return await repo.preview.regenerate({
      idOrPath: document.idOrPath,
      abortController
    });
  },
  saveChanges: async ({
    document,
    abortController
  }) => {
    const reqBody = {
      Shapes: JSON.stringify([{
        redactions: document.shapes.redactions
      }, {
        highlights: document.shapes.highlights
      }, {
        annotations: document.shapes.annotations
      }])
    };
    await repo.patch({
      idOrPath: document.idOrPath,
      content: reqBody,
      requestInit: {
        signal: abortController.signal
      }
    });
  },
  getDocumentData: async settings => {
    const documentData = (await repo.load({
      idOrPath: settings.idOrPath,
      oDataOptions: {
        select: 'all'
      }
    })).d;
    return {
      idOrPath: settings.idOrPath,
      hostName: repo.configuration.repositoryUrl,
      fileSizekB: documentData.Size,
      pageCount: toNumber(documentData.PageCount, -1),
      documentName: documentData.DisplayName || '',
      documentType: documentData.Type || 'File',
      shapes: documentData.Shapes && {
        redactions: JSON.parse(documentData.Shapes)[0].redactions.map(a => addGuidToShape(a)) || [],
        annotations: JSON.parse(documentData.Shapes)[2].annotations.map(a => addGuidToShape(a)) || [],
        highlights: JSON.parse(documentData.Shapes)[1].highlights.map(a => addGuidToShape(a)) || []
      } || {
        redactions: [],
        annotations: [],
        highlights: []
      }
    };
  },
  isPreviewAvailable: async ({
    document,
    version,
    page,
    abortController
  }) => {
    return await repo.preview.available({
      document,
      version,
      page,
      abortController
    });
  },
  canEditDocument: async ({
    idOrPath,
    abortController
  }) => {
    const response = await repo.security.hasPermission(idOrPath, ['Save'], undefined, {
      signal: abortController.signal,
      credentials: 'include'
    });
    return response;
  },
  canHideRedaction: async ({
    idOrPath,
    abortController
  }) => await repo.security.hasPermission(idOrPath, ['PreviewWithoutRedaction'], undefined, {
    signal: abortController.signal,
    credentials: 'include'
  }),
  canHideWatermark: async ({
    idOrPath,
    abortController
  }) => await repo.security.hasPermission(idOrPath, ['PreviewWithoutWatermark'], undefined, {
    signal: abortController.signal,
    credentials: 'include'
  }),
  getExistingPreviewImages: async ({
    document,
    version,
    abortController
  }) => {
    return await repo.preview.getExistingImages({
      document,
      version,
      abortController
    });
  },
  commentActions: {
    addPreviewComment: async ({
      document,
      comment,
      abortController
    }) => {
      const response = await repo.preview.addComment({
        idOrPath: document.idOrPath,
        comment,
        abortController
      });
      return [response].map(changeCreatedByUrlToCurrent(document))[0];
    },
    deletePreviewComment: async ({
      document,
      commentId,
      abortController
    }) => {
      return await repo.preview.deleteComment({
        idOrPath: document.idOrPath,
        commentId,
        abortController
      });
    },
    getPreviewComments: async ({
      document,
      page,
      abortController
    }) => {
      const response = await repo.preview.getComments({
        idOrPath: document.idOrPath,
        page,
        abortController
      });
      return response.map(changeCreatedByUrlToCurrent(document));
    }
  }
});
export const DocumentViewerApiSettingsProvider = ({
  children,
  options
}) => {
  const repo = useRepository();
  const settings = useMemo(() => deepMerge(createDefaultApiSettings(repo), options), [repo, options]);
  return /*#__PURE__*/React.createElement(DocumentViewerApiSettingsContext.Provider, {
    value: settings
  }, children);
};
//# sourceMappingURL=api-settings.js.map